// TanStack Query hooks for the financial suite: lookups, vendors, projects,
// expenses, payments (income ledger), retainers, contractor payments and the
// CSV report downloads. Mirrors packages/api/src/routes/{lookups,vendors,
// projects,expenses,payments,retainers,contractor-payments,reports}.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ContractorPayment,
  ContractorPaymentResponse,
  ContractorPaymentsListResponse,
  CreateContractorPaymentInput,
  CreateExpenseInput,
  CreatePaymentInput,
  CreateProjectInput,
  CreateRetainerInput,
  CreateVendorInput,
  Expense,
  ExpenseResponse,
  ExpensesListResponse,
  LookupOptionsListResponse,
  Payment,
  PaymentResponse,
  PaymentsListResponse,
  Project,
  ProjectFinancialSummary,
  ProjectResponse,
  ProjectStatus,
  ProjectType,
  ProjectsListResponse,
  Retainer,
  RetainerResponse,
  RetainersListResponse,
  UpdateExpenseInput,
  UpdateProjectInput,
  UpdateVendorInput,
  Vendor,
  VendorResponse,
  VendorsListResponse,
} from '@dsr/shared-types';
import { api, downloadBlob, uploadFile } from '../client';

// =============================================================================
// Lookup options
// =============================================================================

export function useLookup(category: 'expense_type' | 'tax_category' | 'payment_method') {
  return useQuery({
    queryKey: ['lookup', category],
    staleTime: 5 * 60_000, // option lists barely change
    queryFn: ({ signal }) =>
      api.get<LookupOptionsListResponse>(`/api/lookups?category=${category}`, signal),
  });
}

// =============================================================================
// Vendors
// =============================================================================

export function useVendors(q?: string) {
  const qs = q && q.trim().length > 0 ? `?q=${encodeURIComponent(q.trim())}` : '';
  return useQuery({
    queryKey: ['vendors', q ?? ''],
    queryFn: ({ signal }) => api.get<VendorsListResponse>(`/api/vendors${qs}`, signal),
  });
}

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVendorInput) =>
      api.post<VendorResponse>('/api/vendors', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}

export interface UpdateVendorVars extends UpdateVendorInput {
  id: number;
}

export function useUpdateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateVendorVars) =>
      api.patch<VendorResponse>(`/api/vendors/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}

export function useDeleteVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/vendors/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}

// =============================================================================
// Projects
// =============================================================================

export interface ProjectFilters {
  organization_id?: number;
  status?: ProjectStatus;
  project_type?: ProjectType;
  q?: string;
}

export function useProjects(filters: ProjectFilters = {}) {
  const params = new URLSearchParams();
  if (filters.organization_id) params.set('organization_id', String(filters.organization_id));
  if (filters.status) params.set('status', filters.status);
  if (filters.project_type) params.set('project_type', filters.project_type);
  if (filters.q && filters.q.trim().length > 0) params.set('q', filters.q.trim());
  const qs = params.toString();
  return useQuery({
    queryKey: ['projects', { ...filters }],
    queryFn: ({ signal }) =>
      api.get<ProjectsListResponse>(`/api/projects${qs ? `?${qs}` : ''}`, signal),
  });
}

export function useProject(id: number | null) {
  return useQuery({
    queryKey: ['project', id],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) => api.get<ProjectResponse>(`/api/projects/${id}`, signal),
  });
}

export function useProjectFinancials(id: number | null) {
  return useQuery({
    queryKey: ['project-financials', id],
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<{ summary: ProjectFinancialSummary }>(`/api/projects/${id}/financials`, signal),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api.post<ProjectResponse>('/api/projects', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export interface UpdateProjectVars extends UpdateProjectInput {
  id: number;
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateProjectVars) =>
      api.patch<ProjectResponse>(`/api/projects/${id}`, patch),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', vars.id] });
    },
  });
}

// =============================================================================
// Expenses
// =============================================================================

export interface ExpenseFilters {
  project_id?: number | 'none';
  expense_type?: string;
  tax_category?: string;
  reimbursable?: 'pending';
  since?: number;
  until?: number;
}

export function useExpenses(filters: ExpenseFilters = {}) {
  const params = new URLSearchParams();
  if (filters.project_id !== undefined) params.set('project_id', String(filters.project_id));
  if (filters.expense_type) params.set('expense_type', filters.expense_type);
  if (filters.tax_category) params.set('tax_category', filters.tax_category);
  if (filters.reimbursable) params.set('reimbursable', filters.reimbursable);
  if (filters.since) params.set('since', String(filters.since));
  if (filters.until) params.set('until', String(filters.until));
  const qs = params.toString();
  return useQuery({
    queryKey: ['expenses', { ...filters }],
    queryFn: ({ signal }) =>
      api.get<ExpensesListResponse>(`/api/expenses${qs ? `?${qs}` : ''}`, signal),
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      api.post<ExpenseResponse>('/api/expenses', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-financials'] });
    },
  });
}

export interface UpdateExpenseVars extends UpdateExpenseInput {
  id: number;
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateExpenseVars) =>
      api.patch<ExpenseResponse>(`/api/expenses/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-financials'] });
    },
  });
}

export function useApproveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ExpenseResponse>(`/api/expenses/${id}/approve`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

export function useReimburseExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ExpenseResponse>(`/api/expenses/${id}/reimburse`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/expenses/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-financials'] });
    },
  });
}

export function useUploadReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      uploadFile<{ expense: Expense }>(`/api/expenses/${id}/receipt`, file),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

export function downloadReceipt(id: number): Promise<void> {
  return downloadBlob(`/api/expenses/${id}/receipt`, `receipt-${id}`);
}

// =============================================================================
// Payments (income ledger)
// =============================================================================

export interface PaymentFilters {
  organization_id?: number;
  project_id?: number;
  invoice_id?: number;
  status?: Payment['status'];
  since?: number;
  until?: number;
}

export function usePayments(filters: PaymentFilters = {}) {
  const params = new URLSearchParams();
  for (const k of ['organization_id', 'project_id', 'invoice_id', 'since', 'until'] as const) {
    const v = filters[k];
    if (v !== undefined) params.set(k, String(v));
  }
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  return useQuery({
    queryKey: ['payments', { ...filters }],
    queryFn: ({ signal }) =>
      api.get<PaymentsListResponse>(`/api/payments${qs ? `?${qs}` : ''}`, signal),
  });
}

export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePaymentInput) =>
      api.post<PaymentResponse>('/api/payments', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['project-financials'] });
    },
  });
}

export function useVoidPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<PaymentResponse>(`/api/payments/${id}/void`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['project-financials'] });
    },
  });
}

// =============================================================================
// Retainers
// =============================================================================

export interface RetainerFilters {
  organization_id?: number;
  project_id?: number;
  active?: boolean;
}

export function useRetainers(filters: RetainerFilters = {}) {
  const params = new URLSearchParams();
  if (filters.organization_id) params.set('organization_id', String(filters.organization_id));
  if (filters.project_id) params.set('project_id', String(filters.project_id));
  if (filters.active) params.set('active', 'true');
  const qs = params.toString();
  return useQuery({
    queryKey: ['retainers', { ...filters }],
    queryFn: ({ signal }) =>
      api.get<RetainersListResponse>(`/api/retainers${qs ? `?${qs}` : ''}`, signal),
  });
}

export function useCreateRetainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRetainerInput & { record_funding_payment?: boolean }) =>
      api.post<RetainerResponse>('/api/retainers', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['retainers'] });
      void qc.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

export function useDeleteRetainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/retainers/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['retainers'] }),
  });
}

// =============================================================================
// Contractor payments
// =============================================================================

export interface ContractorPaymentFilters {
  personnel_id?: number;
  project_id?: number;
  tax_year?: number;
  status?: ContractorPayment['status'];
}

export function useContractorPayments(filters: ContractorPaymentFilters = {}) {
  const params = new URLSearchParams();
  for (const k of ['personnel_id', 'project_id', 'tax_year'] as const) {
    const v = filters[k];
    if (v !== undefined) params.set(k, String(v));
  }
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  return useQuery({
    queryKey: ['contractor-payments', { ...filters }],
    queryFn: ({ signal }) =>
      api.get<ContractorPaymentsListResponse>(
        `/api/contractor-payments${qs ? `?${qs}` : ''}`, signal),
  });
}

export interface Ten99Row {
  personnel_id: number;
  personnel_name: string;
  payment_count: number;
  paid_total: string;
}

export function use1099Summary(taxYear: number) {
  return useQuery({
    queryKey: ['1099-summary', taxYear],
    queryFn: ({ signal }) =>
      api.get<{ tax_year: number; rows: Ten99Row[] }>(
        `/api/contractor-payments/1099-summary?tax_year=${taxYear}`, signal),
  });
}

export function useCreateContractorPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractorPaymentInput) =>
      api.post<ContractorPaymentResponse>('/api/contractor-payments', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contractor-payments'] });
      void qc.invalidateQueries({ queryKey: ['1099-summary'] });
      void qc.invalidateQueries({ queryKey: ['project-financials'] });
    },
  });
}

export function useVoidContractorPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ContractorPaymentResponse>(`/api/contractor-payments/${id}/void`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contractor-payments'] });
      void qc.invalidateQueries({ queryKey: ['1099-summary'] });
    },
  });
}

// =============================================================================
// CSV reports
// =============================================================================

function rangeQs(since: number | null, until: number | null): string {
  const params = new URLSearchParams();
  if (since) params.set('since', String(since));
  if (until) params.set('until', String(until));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function downloadExpensesByTaxCsv(since: number | null, until: number | null): Promise<void> {
  return downloadBlob(`/api/reports/expenses-by-tax${rangeQs(since, until)}`, 'expenses-by-tax-category.csv');
}

export function downloadRevenueCsv(since: number | null, until: number | null): Promise<void> {
  return downloadBlob(`/api/reports/revenue${rangeQs(since, until)}`, 'revenue.csv');
}

export function downloadProjectProfitabilityCsv(since: number | null, until: number | null): Promise<void> {
  return downloadBlob(`/api/reports/project-profitability${rangeQs(since, until)}`, 'project-profitability.csv');
}

// Re-export commonly paired wire types.
export type { Project, Vendor, Expense, Payment, Retainer, ContractorPayment };
